import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';
import type { BulkMoveItemFailure, BulkMoveResult } from '@bower/shared';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { WORKSPACE_ROOT_PARENT } from '../../lib/queryClient';
import { apiService } from '../../services/api.service';
import { ApiResponseError, unwrapApiResponse } from '../../services/api/http-client.types';
import {
  bulkMoveWorkspaceItems,
  type WorkspaceItemsPage,
} from '../../services/api/workspaceItems.api';
import { safeAnalytics } from '../../utils/analytics';
import { countBucket } from '../../utils/analyticsCountBucket';
import { bulkMoveBody, useBulkMoveUndo, type MovableItem, type MovedItem } from './useBulkMoveUndo';
import { FOLDER_WORD, getEntityPluralName } from '../../theme/entityIcons';
import { refreshFolderLists } from '../../lib/folderListRefresh';

export interface BulkMoveVariables {
  /** The rows being moved, as the list holds them (their `parentId` is where they are now). */
  readonly items: ReadonlyArray<MovableItem>;
  /** Destination folder; null = the workspace root. */
  readonly targetFolderId: string | null;
  /** Destination name for the toast; optional. */
  readonly targetName?: string;
}

type Pages = InfiniteData<WorkspaceItemsPage, string | undefined>;
type Snapshot = ReadonlyArray<readonly [QueryKey, Pages | undefined]>;

const parentOf = (item: MovableItem) => item.parentId ?? WORKSPACE_ROOT_PARENT;

/** Every page with the moved rows taken out, and page 1's total reduced to match. */
function withoutRows(data: Pages | undefined, ids: ReadonlySet<string>): Pages | undefined {
  if (!data) return data;
  let removed = 0;
  const pages = data.pages.map((page) => {
    const items = page.items.filter((item) => !ids.has(item.id));
    removed += page.items.length - items.length;
    return { ...page, items };
  });
  const [first] = pages;
  if (first?.totalCount != null) pages[0] = { ...first, totalCount: first.totalCount - removed };
  return { ...data, pages };
}

const isFailure = (v: unknown): v is BulkMoveItemFailure =>
  typeof v === 'object' && v !== null && 'id' in v && 'type' in v && 'reason' in v;

/** The per-item refusals a failed bulk move carried (`details.items`); empty otherwise. */
export function bulkMoveFailures(error: unknown): BulkMoveItemFailure[] {
  if (!(error instanceof ApiResponseError)) return [];
  const items = error.details?.items;
  return Array.isArray(items) ? items.filter(isFailure) : [];
}

const FAILURE_WORDS: Record<BulkMoveItemFailure['reason'], string> = {
  NOT_FOUND: 'no longer available',
  FORBIDDEN: 'you only have view access',
  CYCLE: `a ${FOLDER_WORD} can't move inside itself`,
};

/** A refused bulk move, per item in the server's words (shared with drag and drop). */
export function moveFailureText(
  error: unknown,
  items: ReadonlyArray<{ readonly id: string; readonly name: string }>,
): string {
  const names = new Map(items.map((item) => [item.id, item.name]));
  const lines = bulkMoveFailures(error).map(
    (f) => `${names.get(f.id) ?? 'An item'}: ${FAILURE_WORDS[f.reason]}`,
  );
  if (lines.length > 0) return `Nothing was moved. ${lines.join('. ')}.`;
  return error instanceof Error ? error.message : 'Nothing was moved.';
}

/**
 * The success toast's second line: where the items went and, when the server
 * renamed folders to avoid a clash there (`result.name`), that it did.
 */
function movedDescription(
  targetName: string | undefined,
  renames: ReadonlyArray<{ readonly from: string; readonly to: string }>,
): string {
  const where = targetName ? `To ${targetName}` : '';
  if (renames.length === 0) return where;
  const renamed =
    renames.length === 1
      ? `Renamed ${renames[0].from} to ${renames[0].to}, as that name was taken there.`
      : `Renamed ${renames.length} ${getEntityPluralName('project').toLowerCase()}, as their names were taken there.`;
  return where ? `${where}. ${renamed}` : renamed;
}

/**
 * Move files and folders into one folder (or the root) in one all-or-nothing
 * request.
 *
 * - Optimistic: the moved rows leave the SOURCE lists at once (each
 *   `children`-scope list of a folder a moved row came from, in every sort and
 *   filter variant).
 * - A refusal restores those lists exactly and exposes the server's reason:
 *   `errorCode` and `failures` (which item, and why: NOT_FOUND, FORBIDDEN,
 *   CYCLE). Nothing moved, so nothing else needs undoing.
 * - On settle, the source and target lists are invalidated either way, and
 *   so is every `descendants` list in the workspace (keyed on an ancestor,
 *   which the move does not name). Those are NOT stripped optimistically: the
 *   cache cannot tell whether the target is still inside that ancestor, so a
 *   strip would flicker out rows that stay in scope.
 * - On success, an undo toast moves the items back with one bulk move per
 *   previous parent (`useBulkMoveUndo`) and renames back any folder the move
 *   renamed. Items the server reports `unchanged` are left alone.
 */
export function useBulkMove() {
  const workspaceId = useWorkspaceId() ?? '';
  const queryClient = useQueryClient();

  const offerUndo = useBulkMoveUndo();

  /** A folder's lists, in every sort/filter variant (partial match); `children` narrows to its own rows. */
  const listsOf = (parent: string, scope?: 'children'): QueryKey => [
    'workspace-items',
    { workspaceId },
    scope ? { parent, scope } : { parent },
  ];

  const mutation = useMutation<BulkMoveResult, Error, BulkMoveVariables, { snapshot: Snapshot }>({
    mutationFn: async ({ items, targetFolderId }) =>
      unwrapApiResponse(
        await bulkMoveWorkspaceItems(apiService, bulkMoveBody(workspaceId, items, targetFolderId)),
        'Could not move these items. Nothing was moved.',
      ),
    // A refusal answers the same on a retry, and a retry delays the rollback.
    retry: false,
    onMutate: async ({ items }) => {
      const ids = new Set(items.map((item) => item.id));
      const sources = [...new Set(items.map(parentOf))];
      await Promise.all(sources.map((p) => queryClient.cancelQueries({ queryKey: listsOf(p) })));
      const snapshot = sources.flatMap((parent) =>
        queryClient.getQueriesData<Pages>({ queryKey: listsOf(parent, 'children') }),
      );
      for (const [key, data] of snapshot) queryClient.setQueryData(key, withoutRows(data, ids));
      return { snapshot };
    },
    onError: (_error, _vars, context) => {
      for (const [key, data] of context?.snapshot ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: (_data, _error, { items, targetFolderId }) => {
      const touched = new Set([...items.map(parentOf), targetFolderId ?? WORKSPACE_ROOT_PARENT]);
      for (const parent of touched) refreshFolderLists(queryClient, { queryKey: listsOf(parent) });
      // A descendants list is keyed on an ancestor the move does not name.
      refreshFolderLists(queryClient, {
        queryKey: ['workspace-items', { workspaceId }, { scope: 'descendants' }],
      });
    },
    onSuccess: ({ results }, { items, targetFolderId, targetName }) => {
      const byId = new Map(items.map((item) => [item.id, item]));
      const renames: Array<{ from: string; to: string }> = [];
      const moved: MovedItem[] = results.flatMap((result) => {
        const item = byId.get(result.id);
        if (result.status !== 'moved' || !item) return [];
        if (result.name === undefined || result.name === item.name) return [{ item }];
        renames.push({ from: item.name, to: result.name });
        return [{ item, originalName: item.name }];
      });
      if (moved.length === 0) return;
      safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_ITEMS_MOVED, {
        count_bucket: countBucket(moved.length),
        target_kind: targetFolderId ? 'project' : 'workspace',
      });
      const noun = moved.length === 1 ? 'item' : 'items';
      offerUndo(`Moved ${moved.length} ${noun}`, movedDescription(targetName, renames), moved);
    },
  });

  return {
    ...mutation,
    errorCode: mutation.error instanceof ApiResponseError ? mutation.error.code : undefined,
    failures: bulkMoveFailures(mutation.error),
  };
}
